import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useContext, useEffect, useRef, useState } from "react";
import Select from "react-select";
import { API_URL } from "../../constants/api";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ModeContext } from "../../App";
import { format } from "date-fns";
import "./AllAdmins.css";

const EditUserLayer = () => {
  const { id } = useParams();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("");
  const [active, setActive] = useState(true);
  const [image, setImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [created, setCreated] = useState("");
  const [username, setUserName] = useState("");
  const [showFullName, setShowFullName] = useState(false);
  const [showUsername, setShowUsername] = useState(false);
  const [isFullNameOverflowing, setIsFullNameOverflowing] = useState(false);
  const [isUsernameOverflowing, setIsUsernameOverflowing] = useState(false);
  const [fullNameTruncated, setFullNameTruncated] = useState("");
  const [usernameTruncated, setUsernameTruncated] = useState("");
  const fullNameRef = useRef(null);
  const usernameRef = useRef(null);
  const measurerRef = useRef(null);
  const [groups, setGroups] = useState([
    {
      label: "User",
      value: "user",
    },
    {
      label: "Garage Owner",
      value: "garage_owner",
    },
  ]);

  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const { mode, setMode } = useContext(ModeContext);

  useEffect(() => {
    const getUser = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}api/admin/users/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const json = await response.json();

        if (response.ok) {
          setFullName(json.data.fullName || "");
          setEmail(json.data.email || "");
          setPhoneNumber(json.data.phoneNumber || "");
          setRole(json.data.role || "");
          setActive(json.data.isActive);
          setCreated(json.data.createdAt);
          setUserName(json.data.username);
          if (json.data.image_url) {
            setImagePreviewUrl(json.data.image_url);
          }
        } else {
          toast.error(json.errorMessage || "Failed to fetch user data");
        }
      } catch (error) {
        console.error(error);
        toast.error("An error occurred while fetching user data");
      }
    };

    getUser();
  }, [id]);

  // Finds the longest prefix of `text` that, together with "... See More",
  // still fits within 2 lines inside `visibleEl` - so the button always lands
  // right after the ellipsis instead of overlapping or wrapping onto a 3rd line.
  const truncateToTwoLines = (text, visibleEl) => {
    const measurer = measurerRef.current;
    if (!text || !visibleEl || !measurer) {
      return { text, truncated: false };
    }

    const styles = window.getComputedStyle(visibleEl);
    measurer.style.fontSize = styles.fontSize;
    measurer.style.fontFamily = styles.fontFamily;
    measurer.style.fontWeight = styles.fontWeight;
    measurer.style.lineHeight = styles.lineHeight;
    measurer.style.letterSpacing = styles.letterSpacing;
    measurer.style.width = `${visibleEl.clientWidth}px`;

    const lineHeightPx =
      parseFloat(styles.lineHeight) || parseFloat(styles.fontSize) * 1.2;
    const maxHeight = lineHeightPx * 2 + 1;

    measurer.textContent = text;
    if (measurer.scrollHeight <= maxHeight) {
      return { text, truncated: false };
    }

    let low = 0;
    let high = text.length;
    let best = "";
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const candidate = text.slice(0, mid).trimEnd();
      measurer.textContent = `${candidate}... See More`;
      if (measurer.scrollHeight <= maxHeight) {
        best = candidate;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    return { text: best, truncated: true };
  };

  useEffect(() => {
    const recompute = () => {
      if (fullNameRef.current) {
        const { text, truncated } = truncateToTwoLines(
          fullName,
          fullNameRef.current,
        );
        setFullNameTruncated(text);
        setIsFullNameOverflowing(truncated);
      }
      if (usernameRef.current) {
        const { text, truncated } = truncateToTwoLines(
          username ? `@${username}` : "",
          usernameRef.current,
        );
        setUsernameTruncated(text);
        setIsUsernameOverflowing(truncated);
      }
    };

    recompute();
    window.addEventListener("resize", recompute);
    return () => window.removeEventListener("resize", recompute);
  }, [fullName, username]);

  // Commented out editable functions
  /*
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
      setImage(file);
    }
  };

  const validateForm = () => {
    let errors = {};
    if (!role) {
      errors.role = "Role is required";
      toast.error("Role required");
    }
    
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validate = await validateForm();
    if (!validate) {
      return;
    }

    const formData = new FormData();
    formData.append("fullName", fullName);
    formData.append("email", email);
    formData.append("phoneNumber", phoneNumber);
    formData.append("isActive", active);
    formData.append("role", role);
    
    // Only append password if it's being changed
    if (password) {
      formData.append("password", password);
    }
    
    if (image) {
      formData.append("image", image);
    }

    try {
      const loading = toast.loading("Updating role...");
      const response = await fetch(`${process.env.REACT_APP_API_URL}api/admin/users/${id}`, {
        method: "PUT",
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const json = await response.json();

      if (response.ok) {
        toast.dismiss(loading);
        toast.success("Role updated successfully");
        navigate("/all-users");
      } else {
        toast.dismiss(loading);
        toast.error(json.errorMessage || "Error updating role");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while updating role");
    }
  };
  */

  const colorStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: mode !== "light" ? "#273142" : "#ffffff",
      borderColor: state.isFocused ? "#blue" : "#ccc",
      boxShadow: state.isFocused ? "0 0 0 1px #blue" : "none",
      "&:hover": {
        borderColor: "#blue",
      },
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#24A97B"
        : state.isFocused
          ? mode !== "light"
            ? "#273142"
            : "#f8f9fa"
          : mode !== "light"
            ? "#273142"
            : "#ffffff",
      color: state.isSelected ? "black" : mode !== "light" ? "white" : "black",
      "&:hover": {
        backgroundColor: mode !== "light" ? "#000000" : "#e9ecef",
      },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: mode !== "light" ? "#273142" : "#ffffff",
      border: "1px solid #ccc",
    }),
    placeholder: (base) => ({
      ...base,
      color: mode !== "light" ? "#ffffff" : "#6c757d",
    }),
    singleValue: (base) => ({
      ...base,
      color: mode !== "light" ? "#ffffff" : "#495057",
    }),
    input: (base) => ({
      ...base,
      color: mode !== "light" ? "#ffffff" : "#495057",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: mode !== "light" ? "#000000" : "#e9ecef",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: mode !== "light" ? "#ffffff" : "#495057",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: mode !== "light" ? "#ffffff" : "#495057",
      "&:hover": {
        backgroundColor: "red",
        color: "white",
      },
    }),
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-body p-24">
        <div className="row justify-content-center">
          <div>
            <div className="card-header bg-base py-3">
              <h6 className="text-lg fw-semibold mb-0">User Information</h6>
            </div>
            <div className="card-body p-24">
              <div className="d-flex align-items-center mb-20">
                <img
                  src={
                    imagePreviewUrl || "/assets/images/user-list/user-list1.png"
                  }
                  alt="Owner"
                  className="w-60-px h-60-px rounded-circle flex-shrink-0 me-3 overflow-hidden"
                />
                <div className="w-100">
                  {/* off-screen node used only to measure text wrapping; never shown */}
                  <span
                    ref={measurerRef}
                    aria-hidden="true"
                    className="text-word-break"
                    style={{
                      position: "absolute",
                      visibility: "hidden",
                      whiteSpace: "normal",
                      pointerEvents: "none",
                      left: "-9999px",
                      top: 0,
                    }}
                  />

                  <h5 ref={fullNameRef} className="mb-0 text-word-break fs-4">
                    {showFullName || !isFullNameOverflowing ? (
                      fullName
                    ) : (
                      <>
                        {fullNameTruncated}...{" "}
                        <button
                          type="button"
                          className="btn btn-link p-0 text-decoration-none"
                          onClick={() => setShowFullName(true)}
                        >
                          See More
                        </button>
                      </>
                    )}
                  </h5>
                  {showFullName && isFullNameOverflowing && (
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-none d-block"
                      onClick={() => setShowFullName(false)}
                    >
                      See Less
                    </button>
                  )}

                  <p
                    ref={usernameRef}
                    className="mb-0 text-muted text-word-break"
                  >
                    {showUsername || !isUsernameOverflowing ? (
                      `@${username}`
                    ) : (
                      <>
                        {usernameTruncated}...{" "}
                        <button
                          type="button"
                          className="btn btn-link p-0 text-decoration-none"
                          onClick={() => setShowUsername(true)}
                        >
                          See More
                        </button>
                      </>
                    )}
                  </p>
                  {showUsername && isUsernameOverflowing && (
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-none d-block"
                      onClick={() => setShowUsername(false)}
                    >
                      See Less
                    </button>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-20">
                    <label className="form-label fw-semibold mb-0">Email</label>
                    <p className="form-control-static py-0">{email}</p>
                  </div>
                  <div className="mb-20">
                    <label className="form-label fw-semibold mb-0">
                      Status
                    </label>
                    <p className="form-control-static py-0">
                      <span
                        className="badge"
                        style={{
                          backgroundColor: active ? "#B9F2CE" : "#E2E5E9",
                          color: active ? "#176638" : "#454B54",
                        }}
                      >
                        {active ? "Active" : "Inactive"}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-20">
                    <label className="form-label fw-semibold mb-0">
                      Phone Number
                    </label>
                    <p className="form-control-static py-0">{phoneNumber}</p>
                  </div>
                  <div className="mb-20">
                    <label className="form-label fw-semibold mb-0">
                      Member Since
                    </label>
                    {created ? (
                      <p className="form-control-static py-0">
                        {format(new Date(created), "dd MMM, yyyy")}
                      </p>
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditUserLayer;
